<script setup lang="ts">
import { useIntervalFn } from '@vueuse/core'
import { AlertTriangle, MessageCircle } from 'lucide-vue-next'
import { ref } from 'vue'

import ChatSheet from '@/components/chat/ChatSheet.vue'

interface Props {
  customerId: number
  customerName: string
  customerPhone: string
  customerFirstName: string
  loanApplicationId?: string | null
}

const props = withDefaults(defineProps<Props>(), {
  loanApplicationId: null,
})

const sheetOpen      = ref(false)
const unreadCount    = ref(0)
const notConnected   = ref(false)

const unreadUrl = `/api/chat/${props.customerId}/unread_count`

async function fetchUnreadCount() {
  if (sheetOpen.value) return  // sheet is open → already marked read
  try {
    const res = await fetch(unreadUrl, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    })

    if (res.status === 503) {
      notConnected.value = true
      return
    }

    if (!res.ok) return
    notConnected.value = false
    const data = await res.json()
    unreadCount.value = data.unread_count ?? 0
  } catch {
    // Silent — badge just doesn't update
  }
}

// Poll every 15 seconds while sheet is closed
useIntervalFn(fetchUnreadCount, 15_000, { immediate: true })

function openSheet() {
  sheetOpen.value = true
  unreadCount.value = 0  // Optimistic clear — server marks read when messages are fetched
}
</script>

<template>
  <!-- Floating action button -->
  <div class="fixed bottom-6 right-6 z-50">
    <button
      class="group relative flex size-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg ring-2 ring-primary/20 transition-all duration-200 hover:scale-105 hover:shadow-xl hover:ring-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 active:scale-95"
      type="button"
      aria-label="Open customer chat"
      :title="notConnected ? 'Podium not connected' : `Chat with ${customerName}`"
      @click="openSheet"
    >
      <MessageCircle class="size-6 transition-transform group-hover:scale-110" />

      <!-- Not-connected warning badge -->
      <span
        v-if="notConnected"
        class="absolute -top-1 -right-1 flex size-5 items-center justify-center rounded-full bg-amber-500 shadow"
        title="Podium not connected"
      >
        <AlertTriangle class="size-3 text-white" />
      </span>

      <!-- Unread badge (only when connected) -->
      <span
        v-else-if="unreadCount > 0"
        class="absolute -top-1 -right-1 flex min-w-5 items-center justify-center rounded-full bg-destructive px-1 py-0.5 text-[10px] font-bold leading-none text-destructive-foreground shadow"
      >
        {{ unreadCount > 99 ? '99+' : unreadCount }}
      </span>
    </button>
  </div>

  <!-- Chat sheet (portal — renders outside the FAB) -->
  <ChatSheet
    v-model:open="sheetOpen"
    :customer-id="customerId"
    :customer-name="customerName"
    :customer-phone="customerPhone"
    :customer-first-name="customerFirstName"
    :loan-application-id="loanApplicationId"
  />
</template>
